/**
 * Styled primitives for GuardRails modals: course selection, forms, floating fields, and screen-type chips.
 * @module Workspace/components/GuardRailsSelectorModal/styles
 */
import styled, {css} from "styled-components";

/** @param {{theme?: {themeId?: string}}} props */
const isLightTheme = (props) => {
  const id = props.theme?.themeId;
  return id === 'light' || id === 'extra-light';
};

export const GuardRailsModal = styled.div`
display: flex;
  width: 100%;
  height: 100%;
  flex-direction: column;
  overflow-y: auto;
  padding-left: 40px;
  padding-right: 30px;
`;


export const HeaderWrapper = styled.div`
  margin-top: 50px;
  display: flex;
  flex: none;
  flex-direction: column;
  align-items: center;
  padding: 0 30px;
  box-sizing: border-box;
  text-align: left;
`;

const baseInputStyles = css`
  align-self: flex-start;
  width: 100%;
  background-color: ${(props) => props.theme['modal.input.bg.color']};
  margin-bottom: 40px;

  color: ${(props) => props.theme['modal.input.color']};
  font-family: inherit;
  line-height: 42px;
  font-size: ${(props) => props.fontSize}px;

  outline: none;
  transition: all 0.3s ease;
  border: 1px solid ${(props) => props.theme['modal.input.border.color']};
  border-style: none none solid none;

  &:focus {
    border: 1px solid ${(props) => props.theme['color.primary']};
    border-style: none none solid none;
    background: none;
  }

  ::placeholder {
    color: ${(props) => props.theme['modal.input.placeholder.color']};
    font-size: ${(props) => props.fontSize}px;
  }
`;

/* Material-style field: label top-left, input below */
export const CourseInputField = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: ${(props) => props.width || '628px'};
  margin-bottom: 40px;

  & input {
    margin-bottom: 0;
  }
`;

export const CourseInputLabel = styled.label`
  display: block;
  text-align: left;
  font-size: 12px;
  line-height: 16px;
  color: ${(props) => props.theme['modal.input.placeholder.color'] || 'rgba(0, 0, 0, 0.6)'};
  margin-bottom: 8px;
  pointer-events: none;
`;

export const CourseInputElement = styled.input`
  height: ${(props) => props.height || 33}px !important;
  width: ${(props) => props.width || 628}px !important;
  text-align: left;
  ${baseInputStyles}
`;

export const PluginTextareaWrapper = styled.div`
  width: 100%;
  textarea {
    ${baseInputStyles}
  }
`;

export const TextWrapDescription = styled.div`
  align-self: flex-start;
  margin-top: 15px;
  line-height: 22px;
  font-size: 16px;
  color: ${(props) => props.theme['modal.description.color']};
`;

export const TextWrapSubheader = styled.div`
  font-family: ${(props) => isLightTheme(props) ? "'Open Sans', sans-serif" : 'inherit'};
  font-weight: ${(props) => isLightTheme(props) ? '400' : '600'};
  line-height: ${(props) => isLightTheme(props) ? '16px' : '30px'};
  font-size: ${(props) => isLightTheme(props) ? '24px' : '22px'};
  margin-bottom: 55px;
  color: ${(props) => props.theme['modal.title.color']};
  text-align: left;
`;

export const TextWrapSubheaderTwo = styled.div`
  align-self: flex-start;
  font-weight: 600;
  font-size: 20px;
  height: 27px;
  margin-bottom: 5px;
  color: ${(props) => props.theme['modal.title.color']};
`;


export const CourseSelectionButton = styled.button`
  width: 1010px;
  height: 101px;
  opacity: 1;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  background: ${(props) => isLightTheme(props)
    ? '#FFFFFF'
    : 'linear-gradient(90deg, rgba(254, 254, 254, 0.9) 0%, rgba(254, 254, 254, 0.3) 100%)'};
  backdrop-filter: ${(props) => isLightTheme(props) ? 'none' : 'blur(40px)'};
  box-shadow: ${(props) => isLightTheme(props)
    ? '0px 3px 16px 2px rgba(13, 0, 77, 0.05), 0px 9px 12px 1px rgba(13, 0, 77, 0.1), 0px 5px 6px -3px rgba(13, 0, 77, 0.15)'
    : 'none'};
  color: ${(props) => isLightTheme(props) ? '#020917' : (props.theme['modal.input.color'] || '#020917')};
  font-family: ${(props) => isLightTheme(props) ? "'Inter', sans-serif" : "'Open Sans', sans-serif"};
  font-weight: 600;
  font-size: ${(props) => isLightTheme(props) ? '20px' : '24px'};
  line-height: ${(props) => isLightTheme(props) ? '150%' : '16px'};
  letter-spacing: ${(props) => isLightTheme(props) ? '0.01em' : '0'};
  vertical-align: middle;
  text-align: left;
  padding: 20px 30px;
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
  transition: background 0.25s ease;

  &:hover,
  &:focus {
    background: ${(props) => isLightTheme(props)
    ? 'linear-gradient(90deg, rgba(0, 0, 0, 0.35) 0%, rgba(0, 0, 0, 0.15) 100%), linear-gradient(117.32deg, #34B9DC 16.81%, #5C71CD 82.74%)'
    : 'linear-gradient(117.32deg, #34B9DC 16.81%, #5C71CD 82.74%)'};
    color: #fff;
    outline: none;
  }
`;

export const OthersSubheader = styled.div`
  font-family: ${(props) => isLightTheme(props) ? "'Inter', sans-serif" : "'Open Sans', sans-serif"};
  font-weight: 400;
  font-size: ${(props) => isLightTheme(props) ? '16px' : '14px'};
  line-height: ${(props) => isLightTheme(props) ? '143%' : '20px'};
  letter-spacing: 0.15px;
  color: ${(props) => isLightTheme(props) ? '#B5B5B5' : 'inherit'};
  opacity: ${(props) => isLightTheme(props) ? '1' : '0.8'};
`;

export const StyledLabel = styled.label`
  position: absolute;
  left: 10px;
  top: 14px;
  color: gray;
  background: white;
  padding: 0 4px;
  transition: 0.2s ease all;
  pointer-events: none;
`;

/* Underline-style input matching Create a Course screen */
export const UnderlineInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  max-width: 629px;
  margin-bottom: 32px;
`;

export const UnderlineInputLabel = styled.label`
  font-size: 14px;
  line-height: 20px;
  color: ${(props) => props.theme['modal.input.placeholder.color'] || 'currentColor'};
  margin-bottom: 8px;
  display: block;
`;

export const UnderlineInput = styled.input`
  width: 100%;
  background: transparent;
  color: ${(props) => props.theme['modal.input.color'] || 'currentColor'};
  font-size: ${(props) => props.fontSize || 16}px;
  line-height: 24px;
  padding: 8px 0;
  outline: none;
  border: none;
  border-bottom: 1px solid ${(props) => props.theme['modal.input.border.color'] || 'currentColor'};
  transition: border-color 0.2s ease;

  &::placeholder {
    color: ${(props) => props.theme['modal.input.placeholder.color'] || 'currentColor'};
  }

  &:focus {
    border-bottom-color: ${(props) => props.theme['color.primary'] || '#34B9DC'};
  }
`;

export const CreateButton = styled.button`
  background: ${(props) => props.theme['color.primary'] || 'rgba(0, 115, 202, 1)'};
  color: #fff;
  font-family: 'Open Sans', sans-serif;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  padding: 12px 15px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  white-space: nowrap;
  transition: opacity 0.2s ease;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }
  &:focus {
    outline: none;
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

export const SuccessOverlay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  gap: 16px;
`;

export const SuccessIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: ${(props) => props.theme['color.primary'] || 'rgba(0, 115, 202, 1)'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  color: #fff;
`;

export const SuccessMessage = styled.div`
  font-family: 'Open Sans', sans-serif;
  font-weight: 600;
  font-size: 22px;
  color: ${(props) => props.theme['modal.title.color'] || '#fff'};
  text-align: center;
`;

export const FormRowWithButton = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 24px;
  width: 100%;
  max-width: 90%;
  margin-bottom: 32px;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const FormRowWithButtonInput = styled.div`
  flex: 1;
  min-width: 0;
`;

export const HeaderWithBack = styled.div`
  display: flex;
  align-items: center;
  align-self: flex-start;
  gap: 0;
  margin-bottom: 65px;
  width: 100%;
  max-width: 628px;
  margin-top: 40px;

  ${TextWrapSubheader} {
    margin-bottom: 0;
  }
`;

export const CreateCourseTitle = styled.span`
  height: 24px;
  opacity: 1;
  font-family: 'Open Sans', sans-serif;
  font-weight: 400;
  font-style: normal;
  font-size: 24px;
  line-height: 16px;
  letter-spacing: 0;
  vertical-align: middle;
  padding-left: 4px;
  color: ${(props) => isLightTheme(props) ? '#000000' : (props.theme['modal.title.color'] || 'currentColor')};
`;

export const BackLink = styled.button`
  display: flex;
  align-items: center;
  background: none;
  border: none;
  color: ${(props) => isLightTheme(props) ? '#000000' : (props.theme['modal.title.color'] || 'currentColor')};
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  line-height: 24px;
  &:focus {
    outline: none;
  }
`;

export const CreateCourseForm = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  box-sizing: border-box;
  text-align: left;
`;

/* Text field styled components – use directly: Wrapper + Label + Input. Uses baseInputStyles. */
export const StyledTextFieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  max-width: ${(props) => props.fullWidth ? '100%' : '628px'};
  margin-bottom: ${(props) => props.marginBottom ?? 24}px;

  & input {
    margin-bottom: 0;
  }
`;

export const StyledTextFieldLabel = styled.label`
  font-size: 14px;
  line-height: 20px;
  color: ${(props) => props.theme['modal.input.placeholder.color'] || 'currentColor'};
  margin-bottom: 8px;
  display: block;
`;

export const StyledTextFieldInput = styled.input`
  height: ${(props) => props.height || 33}px !important;
  width: 100%;
  text-align: left;
  line-height: 24px;
  padding: 8px 0;
  ${baseInputStyles}
`;

/* MUI-style floating label field — define Label first so Input & Wrapper can reference it */
export const FloatingFieldLabel = styled.label`
  position: absolute;
  left: 0;
  top: 0;
  font-family: 'Open Sans', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 12px;
  letter-spacing: 0;
  color: ${(props) => isLightTheme(props)
    ? 'rgba(0, 0, 0, 0.6)'
    : (props.theme['modal.input.placeholder.color'] || 'rgba(255,255,255,0.5)')};
  pointer-events: none;
  transition: all 0.2s ease;
  opacity: 0;
`;

export const FloatingFieldInput = styled.input`
  width: 100%;
  padding: 8px 0;
  font-family: 'Open Sans', sans-serif;
  font-weight: 400;
  font-size: ${(props) => props.fontSize || 16}px;
  line-height: 16px;
  letter-spacing: 0;
  color: ${(props) => isLightTheme(props) ? '#000000' : (props.theme['modal.input.color'] || '#fff')};
  -webkit-text-fill-color: ${(props) => isLightTheme(props) ? '#000000' : (props.theme['modal.input.color'] || '#fff')};
  background: transparent;
  background-color: transparent !important;
  border: none;
  border-bottom: 1px solid ${(props) => isLightTheme(props)
    ? 'rgba(0, 0, 0, 0.42)'
    : (props.theme['modal.input.border.color'] || 'rgba(217, 217, 217, 1)')};
  outline: none;
  transition: border-color 0.2s ease;
  caret-color: ${(props) => isLightTheme(props) ? '#000000' : (props.theme['modal.input.color'] || '#fff')};

  &:focus {
    border-bottom-color: ${(props) => props.theme['color.primary'] || '#34B9DC'};
    background-color: transparent !important;
  }

  &::placeholder {
    font-family: 'Open Sans', sans-serif;
    font-weight: 400;
    font-size: 16px;
    line-height: 16px;
    color: ${(props) => isLightTheme(props)
    ? 'rgba(0, 0, 0, 0.6)'
    : (props.theme['modal.input.placeholder.color'] || 'rgba(255,255,255,0.5)')};
  }

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus {
    -webkit-box-shadow: 0 0 0 1000px transparent inset;
    -webkit-text-fill-color: ${(props) => isLightTheme(props) ? '#000000' : (props.theme['modal.input.color'] || '#fff')};
    background-color: transparent !important;
    transition: background-color 5000s ease-in-out 0s;
  }
`;

export const FloatingFieldWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: ${(props) => props.fullWidth ? '100%' : '628px'};
  margin-bottom: ${(props) => props.marginBottom ?? 33}px;
  padding-top: 16px;

  ${FloatingFieldInput}:focus + ${FloatingFieldLabel},
  ${FloatingFieldInput}:not(:placeholder-shown) + ${FloatingFieldLabel} {
    opacity: 1;
    top: 0;
    font-size: 12px;
    line-height: 12px;
    color: ${(props) => props.theme['color.primary'] || '#0073CA'};
  }

  ${FloatingFieldInput}:not(:focus):not(:placeholder-shown) + ${FloatingFieldLabel} {
    color: ${(props) => isLightTheme(props)
    ? 'rgba(0, 0, 0, 0.6)'
    : (props.theme['modal.input.placeholder.color'] || 'rgba(255,255,255,0.5)')};
  }
`;



export const InputWrapper = styled.div`
  position: relative;
  width: 280px;
  font-family: Arial, sans-serif;
`;

export const StyledInput = styled.input`
  width: 100%;
  padding: 16px 12px 14px 12px;
  font-size: 16px;
  border-radius: 4px;
  outline: none;
  background: transparent;
  transition: border-color 0.2s ease;

  &:focus {
    border-color: #1976d2;
  }

  /* Move label when focused or has value */
  &:focus + label,
  &:not(:placeholder-shown) + label {
    top: -8px;
    font-size: 12px;
    color: #1976d2;
  }
`;

export const StyledInputLabel = styled.label`
  position: absolute;
  left: 12px;
  top: 16px;
  color: #757575;
  font-size: 16px;
  pointer-events: none;
  background: white;
  padding: 0 4px;
  transition: all 0.2s ease;
`;

export const ScreenTypeSelectorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  max-width: 628px;
  margin-bottom: 24px;
`;

export const ScreenTypeSelectorLabel = styled.label`
  font-size: 14px;
  line-height: 20px;
  color: ${(props) => props.theme['modal.input.placeholder.color'] || 'currentColor'};
  margin-bottom: 12px;
  display: block;
`;

export const ScreenTypeOptions = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

export const ScreenTypeChip = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 12px 20px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 140px;
  text-align: left;
  border: 1.5px solid ${(props) =>
    props.selected
      ? props.theme['color.primary'] || '#34B9DC'
      : props.theme['modal.input.border.color'] || 'rgba(255,255,255,0.2)'};
  background: ${(props) =>
    props.selected
      ? (props.theme['color.primary'] || '#34B9DC') + '1A'
      : 'transparent'};
  color: ${(props) =>
    props.selected
      ? props.theme['color.primary'] || '#34B9DC'
      : props.theme['modal.input.color'] || 'currentColor'};

  &:hover {
    border-color: ${(props) => props.theme['color.primary'] || '#34B9DC'};
  }
  &:focus {
    outline: none;
  }
`;

export const ScreenTypeChipName = styled.span`
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
`;

export const ScreenTypeChipDescription = styled.span`
  font-size: 11px;
  font-weight: 400;
  line-height: 16px;
  margin-top: 4px;
  opacity: 0.7;
`;
